import React from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { FileText } from "lucide-react";
import { Toaster } from "@jem-open/jem-ui";
import AuthenticationRouter from "../features/auth/AuthenticationRouter";
import HomeRouter from "../features/home/HomeRouter";
import AnnouncementRouter from "../features/announcements/AnnouncementRouter";
import MultimessagingRouter from "../features/multimessaging/MultimessagingRouter";
import SurveyRouter from "../features/surveys/SurveyRouter";
import PayslipRouter from "../features/payslips/PayslipRouter";
import EmployeeRouter from "../features/employees/EmployeeRouter";
import AppLayout from "../themes/AppLayout";

import TagsRouter from "../features/tags/TagsRouter";
import Layout from "../themes/Layout";
import ReconsRouter from "../features/recons/ReconsRouter";
import SettingsRouter from "../features/settings/SettingsRouter";
import IRP5Router from "../features/irp5/IRP5Router";
import OdpManagementRouter from "../features/odp-management/OdpManagementRouter";
import NotFound from "../common/pages/NotFound";
import AccessPermissionsProvider from "./AccessPermissionsProvider";
import HelpCenterRouter from "../features/help-center/HelpCenterRouter";
import ComingSoonRouter from "../features/coming-soon/ComingSoonRouter";
import { createEmployeeImporterMovedRoutes } from "../features/employees/EmployeeImporterMovedRoutes";
import { EMPLOYEE_IMPORTER_PATHS } from "../features/employees/EmployeeImporterMovedPaths";
import RosterRouter from "../features/roster/RosterRouter";
import TimesheetRouter from "../features/timesheet/TimesheetRouter";
import InboxQueryRouter from "../features/inbox/InboxQueryRouter";
import SecureDocumentUploadsRouter from "../features/secure_document_uploads/SecureDocumentUploadsRouter";
import SegmentsRouter from "../features/segments/SegmentsRouter";
import TimeAttendanceRouter from "../features/time-attendance/TimeAttendanceRouter";
import ReportPreviewPage from "../features/reports/pages/ReportPreviewPage";
import AuthProvider from "./AuthProvider";
import FeatureFlagProvider from "../common/feature-flagging/FeatureFlagProvider";
import { MigrationStatusProvider } from "../common/migration";

export interface NavigationLinkProps {
  id: string;
  title: string;
  description: string;
  path: string;
  component?: React.FC;
  // eslint-disable-next-line
  icon?: any;
  auth: boolean;
  sidebar?: boolean;
  secondarySidebar?: boolean;
  children?: NavigationLinkProps[];
  scope?: string[];
  requiredFeature: string | string[];
  requiredPermissions: string[];
  requiredCapability?: string | string[];
  isFeatureFlagged?: boolean;
  requiredFeatureFlag?: string;
  isExternal?: boolean;
  /**
   * When true the route renders normally on mobile viewports — `PageContainer`'s
   * mobile blocker overlay and the `DesktopOnlyBanner` are both suppressed.
   * Default falsey: route is desktop-only.
   */
  mobileAvailable?: boolean;
}

const myDocumentsRoute: NavigationLinkProps[] = [
  {
    id: "1d790fe5-54e5-47da-981b-706db1b489de",
    title: "My Documents",
    description: "",
    path: "#",
    auth: true,
    sidebar: true,
    icon: FileText,
    component: () => <div />,
    requiredFeature: "ANY",
    requiredPermissions: ["ANY"],
    children: [
      ...PayslipRouter,
      ...RosterRouter,
      ...TimesheetRouter,
      ...IRP5Router,
      ...SecureDocumentUploadsRouter,
    ],
  },
];

export const APP_ROUTES: NavigationLinkProps[] = [
  ...HomeRouter,
  ...ReconsRouter,
  ...AnnouncementRouter,
  ...MultimessagingRouter,
  ...SurveyRouter,
  ...myDocumentsRoute,
  ...InboxQueryRouter,
  ...EmployeeRouter,
  ...AuthenticationRouter,
  ...TagsRouter,
  ...SettingsRouter,
  ...OdpManagementRouter,
  ...ComingSoonRouter,
  ...HelpCenterRouter,
  ...SegmentsRouter,
  ...TimeAttendanceRouter,
  ...EMPLOYEE_IMPORTER_PATHS.map((path) => ({
    id: path,
    title: "Employee Importer",
    description: "",
    path,
    auth: true,
    sidebar: false,
    secondarySidebar: false,
    requiredFeature: "ANY",
    requiredPermissions: ["ANY"],
    mobileAvailable: true,
  })),
];

const PROTECTED_ROUTER = APP_ROUTES.filter((it) => it.auth);
const PUBLIC_ROUTER = APP_ROUTES.filter((it) => !it.auth);

const RouterProvider: React.FC = () => (
  <Router>
    <Routes>
      {/* Chromeless authed route — renders without app shell (sidebar/header). */}
      <Route
        path="/reports/preview"
        element={
          <AuthProvider>
            <FeatureFlagProvider>
              <MigrationStatusProvider>
                <>
                  <ReportPreviewPage />
                  <Toaster position="bottom-right" />
                </>
              </MigrationStatusProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        }
      />
      <Route element={<Layout />}>
        {PUBLIC_ROUTER.map((it) => (
          <Route
            path={it.path}
            key={it.path}
            element={it.component ? <it.component /> : null}
          />
        ))}
      </Route>

      <Route path="/" element={<AppLayout />}>
        {PROTECTED_ROUTER.map((it) => {
          if (it.isExternal) return null;

          let routes = null;

          if (it.children) {
            routes = it.children
              .filter(
                (
                  child
                ): child is Required<Pick<typeof child, "component">> &
                  typeof child => Boolean(child.component)
              )
              .map((child) => (
                <Route
                  path={child.path}
                  key={child.path}
                  element={
                    <AccessPermissionsProvider
                      path={child.path}
                      requiredFeature={child.requiredFeature}
                      requiredPermissions={child.requiredPermissions}
                      requiredCapability={child.requiredCapability}
                      requiredFeatureFlag={child.requiredFeatureFlag}
                      isFeatureFlagged={child.isFeatureFlagged}
                    >
                      <child.component />
                    </AccessPermissionsProvider>
                  }
                />
              ));
          } else if (it.component) {
            routes = (
              <Route
                path={it.path}
                key={it.path}
                element={
                  <AccessPermissionsProvider
                    path={it.path}
                    requiredFeature={it.requiredFeature}
                    requiredPermissions={it.requiredPermissions}
                    requiredCapability={it.requiredCapability}
                    requiredFeatureFlag={it.requiredFeatureFlag}
                    isFeatureFlagged={it.isFeatureFlagged}
                  >
                    <it.component />
                  </AccessPermissionsProvider>
                }
              />
            );
          }

          return routes;
        })}
        <Route
          path="/employeedataimport"
          element={<Navigate to="/employees#imports" />}
        />
        <Route path="/tags" element={<Navigate to="/employees#tags" />} />
        <Route path="/forms" element={<Navigate to="/surveys" />} />
        <Route path="/employeeleave" element={<Navigate to="/leave" />} />
        {createEmployeeImporterMovedRoutes()}
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Router>
);

export default RouterProvider;
